import type React from "react";

import { clamp } from "lodash";
import { useLayoutEffect, useState } from "react";

interface Point {
  x: number;
  y: number;
}

interface Layout {
  width: number;
  height: number;
  viewWidth: number;
  viewHeight: number;
  anchor: DOMRect | null;
}

const DOCK_HANDLES = '[data-slot="dialog-title"], [data-dock-handle]';

const EDGE = 16;
const ARROW_STEP = 20;

const ARROWS: Record<string, Point> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};

/** Module state: every docked dialog opens here until the page reloads. */
let lastPlaced: Point | null = null;

/**
 * Until it is moved, a docked dialog opens against `anchor`'s right edge,
 * centred on it, or keeps its CSS position when it has none. Either way it is
 * refitted to the viewport whenever the card or the window changes size, so a
 * form taller or wider than the one that was moved never runs off the screen.
 */
export function useDockedPosition(
  card: HTMLElement | null,
  anchor: HTMLElement | null,
) {
  const [layout, setLayout] = useState<Layout | null>(null);
  const [placed, setPlaced] = useState(lastPlaced);
  const [grab, setGrab] = useState<Point | null>(null);

  // A layout effect, so a remembered spot is refitted before the first paint.
  useLayoutEffect(() => {
    if (!card) return;
    const measure = () => setLayout(measureIn(card, anchor));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(card);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [card, anchor]);

  const moveTo = (x: number, y: number) => {
    if (!layout) return;
    const next = fit({ x, y }, layout);
    lastPlaced = next;
    setPlaced(next);
  };

  const position =
    layout && (placed ? fit(placed, layout) : besideAnchor(layout));

  const endDrag = () => setGrab(null);

  return {
    position,
    dragging: grab !== null,
    contentProps: {
      onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
        if (e.button !== 0 || !(e.target as Element).closest(DOCK_HANDLES)) {
          return;
        }
        e.preventDefault();
        const r = e.currentTarget.getBoundingClientRect();
        setGrab({ x: e.clientX - r.left, y: e.clientY - r.top });
        e.currentTarget.setPointerCapture(e.pointerId);
      },
      onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
        if (grab) moveTo(e.clientX - grab.x, e.clientY - grab.y);
      },
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      const arrow = ARROWS[e.key];
      if (!arrow || !card) return;
      e.preventDefault();
      const r = card.getBoundingClientRect();
      const from = position ?? { x: r.left, y: r.top };
      moveTo(from.x + arrow.x * ARROW_STEP, from.y + arrow.y * ARROW_STEP);
    },
  };
}

function measureIn(card: HTMLElement, anchor: HTMLElement | null): Layout {
  return {
    width: card.offsetWidth,
    height: card.offsetHeight,
    viewWidth: window.innerWidth,
    viewHeight: window.innerHeight,
    anchor: anchor?.getBoundingClientRect() ?? null,
  };
}

/** Left edge on the anchor's right edge, centred on it — pushed back on screen
 *  when the anchor leaves no room beside it. */
function besideAnchor(l: Layout): Point | null {
  if (!l.anchor) return null;
  return fit(
    { x: l.anchor.right, y: l.anchor.top + (l.anchor.height - l.height) / 2 },
    l,
  );
}

/** `clamp` keeps the lower bound when the range is empty: a card larger than
 *  the viewport keeps its top-left on screen. */
function fit(p: Point, s: Layout): Point {
  return {
    x: clamp(p.x, EDGE, s.viewWidth - s.width - EDGE),
    y: clamp(p.y, EDGE, s.viewHeight - s.height - EDGE),
  };
}
